/**
 * i18next: RU и KK — равноправные языки (ТЗ 14.1, НФТ-09). Ресурсы — JSON в
 * `src/i18n/locales/<язык>/<namespace>.json`, по одному namespace на раздел; новые файлы
 * подхватываются автоматически. Проверка полноты — `npm run i18n:check`.
 *
 * Все переводы — отдельные чанки, загружается только текущий язык (НФТ-06: первый экран быстрее).
 * Базовые namespaces (CORE_NAMESPACES) загружаются до первой отрисовки (`loadCoreNamespaces`),
 * namespaces разделов — вместе с разделом. Запасного языка нет: полноту обоих языков гарантирует i18n:check.
 */
import i18next, { type BackendModule, type i18n as I18nInstance, type ResourceKey } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { isAppLocale, type AppLocale } from '@/shared/format/locale-data';
import { readStorage, STORAGE_KEYS, writeStorage } from '@/shared/lib/storage';

/** Namespaces, нужные на любом экране: загружаются до первой отрисовки. */
export const CORE_NAMESPACES = ['common', 'ui', 'shell', 'errors', 'auth'] as const;

type LocaleModule = { default: Record<string, unknown> };

const localeModules = import.meta.glob<LocaleModule>('./locales/*/*.json');

function parsePath(path: string): { language: string; namespace: string } | null {
  const match = /\.\/locales\/([a-z]{2})\/([\w-]+)\.json$/.exec(path);
  return match ? { language: match[1] as string, namespace: match[2] as string } : null;
}

/** Все известные namespaces (базовые и разделов). */
export const NAMESPACES: string[] = [...new Set(Object.keys(localeModules).map((path) => parsePath(path)?.namespace).filter((ns): ns is string => Boolean(ns)))].sort();

export const DEFAULT_NAMESPACE = 'common';
export const FALLBACK_LOCALE: AppLocale = 'ru';

/** Загрузка namespace раздела отдельным чанком (плагин backend i18next). */
const lazyBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read: (language, namespace, callback) => {
    const loader = localeModules[`./locales/${language}/${namespace}.json`];
    if (!loader) {
      callback(null, {});
      return;
    }
    loader()
      .then((module) => callback(null, module.default as ResourceKey))
      .catch((error: unknown) => callback(error instanceof Error ? error : new Error(String(error)), null));
  },
};

/** Язык при старте: ?lang= в адресе → сохранённый выбор → язык браузера → русский. */
export function detectInitialLocale(): AppLocale {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang');
    if (isAppLocale(fromUrl)) return fromUrl;
  } catch {
    /* нет адреса — например, в тестах */
  }
  const stored = readStorage(STORAGE_KEYS.locale);
  if (isAppLocale(stored)) return stored;
  const browser = typeof navigator !== 'undefined' ? navigator.language.toLowerCase() : '';
  return browser.startsWith('kk') ? 'kk' : 'ru';
}

function applyDocumentLanguage(locale: string): void {
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

export interface CreateI18nOptions {
  locale?: AppLocale;
  /** Загрузить все namespaces сразу (тесты). */
  preloadAll?: boolean;
}

/** Создаёт экземпляр i18next (приложение — один раз при запуске, тесты — отдельно). */
export function createI18n(options: CreateI18nOptions = {}): I18nInstance {
  const locale = options.locale ?? detectInitialLocale();
  const instance = i18next.createInstance();
  void instance
    .use(lazyBackend)
    .use(initReactI18next)
    .init({
      lng: locale,
      fallbackLng: false,
      supportedLngs: ['ru', 'kk'],
      load: 'currentOnly',
      ns: [...CORE_NAMESPACES],
      defaultNS: DEFAULT_NAMESPACE,
      fallbackNS: DEFAULT_NAMESPACE,
      initAsync: false,
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    });
  applyDocumentLanguage(locale);
  instance.on('languageChanged', (language) => {
    applyDocumentLanguage(language);
    if (isAppLocale(language)) writeStorage(STORAGE_KEYS.locale, language);
  });
  if (options.preloadAll) void instance.loadNamespaces(NAMESPACES);
  return instance;
}

/** Базовые переводы текущего языка — до первой отрисовки приложения. */
export function loadCoreNamespaces(instance: I18nInstance): Promise<void> {
  return instance.loadNamespaces([...CORE_NAMESPACES]);
}

export function currentLocale(instance: I18nInstance): AppLocale {
  const language = instance.resolvedLanguage ?? instance.language;
  return isAppLocale(language) ? language : FALLBACK_LOCALE;
}

/** Namespace из ключа `namespace:key`. */
export function namespaceOf(key: string): string {
  return key.includes(':') ? (key.split(':')[0] as string) : DEFAULT_NAMESPACE;
}
